import React, { useState, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * CONSOLIDATED FRONTEND EXPERIENCE
 * Polished UI: Full width title, dominant canvas, smart cart payload.
 */

const frontendStyles = `
  .eg-slot {
    position: absolute;
    border: 2px dashed rgba(255,255,255,0.5);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .eg-slot:hover {
    border-color: rgba(255,255,255,1);
    background: rgba(255,255,255,0.1);
  }
  .eg-slot.active {
    border: 3px solid var(--eg-accent, #2563eb);
    box-shadow: 0 0 15px rgba(0,0,0,0.2);
    background: rgba(255,255,255,0.2);
    animation: pulse-slot 2s infinite;
  }
  .eg-slot.locked {
    border-color: transparent !important;
    background: transparent !important;
    cursor: default;
  }
  .eg-slot.locked:hover {
    box-shadow: none;
  }
  @keyframes pulse-slot {
    0% { box-shadow: 0 0 0 0 var(--eg-accent-light); }
    70% { box-shadow: 0 0 0 10px rgba(0,0,0,0); }
    100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
  }
  .eg-slot img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    pointer-events: none;
  }
  .eg-slot-empty-text {
    color: rgba(255,255,255,0.8);
    font-size: 14px;
    font-weight: 800;
    text-transform: uppercase;
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
    pointer-events: none;
    text-align: center;
    line-height: 1.2;
  }
  
  .hide-scrollbar::-webkit-scrollbar { display: none; }
  .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
`;

const Icon = ({ name, size = 18, className = "" }) => {
  const icons = {
    shoppingCart: <path d="M9 20a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm7 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-7-3h7a2 2 0 0 0 2-1.61l1.27-6A2 2 0 0 0 20 7H5.21l-.94-2H1" />,
    chevronLeft: <polyline points="15 18 9 12 15 6" />,
    layout: <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />,
    flip: <path d="M21 12H3m12-9l6 9-6 9M6 3l-3 9 3 9" />
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{icons[name] || null}</svg>;
};

const FrontendApp = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [lumiseData, setLumiseData] = useState({ clipart: [] });
  
  const [templateNumber, setTemplateNumber] = useState(1);
  const [activeCategory, setActiveCategory] = useState(null); 
  const [quantities, setQuantities] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);

  const [productBases, setProductBases] = useState([]);
  const [activeBaseId, setActiveBaseId] = useState(null);
  const [layouts, setLayouts] = useState([]);
  const [slots, setSlots] = useState([]);
  const [activeSlotId, setActiveSlotId] = useState(null);

  const POST_ID = typeof window !== 'undefined' && window.EG_FRONTEND?.post_id ? window.EG_FRONTEND.post_id : '';
  const BASE_URL = typeof window !== 'undefined' && window.EG_FRONTEND?.rest_url ? window.EG_FRONTEND.rest_url : '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!BASE_URL || !POST_ID) throw new Error("Context missing");
        const configRes = await fetch(`${BASE_URL}editor-generator/v1/config?post_id=${POST_ID}`);
        const configData = await configRes.json();
        const assetsRes = await fetch('https://capfancy.com/wp-json/lumise/v1/full-assets');
        const assetsData = await assetsRes.json();

        if (configData.success) {
          processData(configData.config, assetsData);
        }
        setLoading(false);
      } catch (err) {
        console.warn("Loading mock data...", err);
        loadMockData();
        setLoading(false);
      }
    };
    fetchData();
  }, [POST_ID, BASE_URL]);

  const processData = (loadedConfig, assetsData) => {
    setConfig(loadedConfig);
    const allClipart = (assetsData.cliparts || []).map(c => {
      let thumb = c.thumbnail_url || c.upload || c.thumbnail || '';
      if (thumb && !thumb.startsWith('http')) {
        thumb = `https://capfancy.com/wp-content/uploads/lumise-data/cliparts/${thumb.replace(/^\/+/, '')}`;
      }
      return { ...c, thumb };
    });
    setLumiseData({ clipart: allClipart });

    const bases = loadedConfig.product_bases || [];
    setProductBases(bases);
    if (bases.length > 0) setActiveBaseId(bases[0].id);

    const rawLayouts = loadedConfig.layouts || [];
    const customLayouts = rawLayouts.map(l => ({
      ...l,
      slots: l.slots.map(s => ({
        ...s,
        categories: s.categories || (s.category ? [s.category] : []),
        isLocked: !!s.isLocked
      }))
    }));
    
    setLayouts(customLayouts);

    if (customLayouts.length > 0) {
      const firstLayout = [...customLayouts].sort((a, b) => a.order - b.order)[0];
      setTemplateNumber(firstLayout.order);
      applyLayout(firstLayout.order, customLayouts);
    }
  };

  const loadMockData = () => {
    const mockConfig = {
      accent_color: '#0ea5e9',
      sizes: 'S, M, L, XL',
      template_mode: 'sort', 
      loading_text: 'PREPARING YOUR EXPERIENCE...',
      background_image: 'https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2000',
      price: 24.99,
      canvas_top: 22, canvas_left: 22, canvas_width: 56, canvas_height: 56,
      product_bases: [
        { id: 'pb_1', name: 'White', color: '#ffffff', image: 'https://placehold.co/800x800/transparent/eee?text=White+Shirt' },
        { id: 'pb_2', name: 'Pink', color: '#fbcfe8', image: 'https://placehold.co/800x800/transparent/eee?text=Pink+Shirt' }
      ],
      layouts: [
        { id: 'l_1', name: 'Center Badge', order: 1, slots: [{ id: 's1', name: 'Badge', categories: [], top: 30, left: 30, width: 40, height: 40 }] },
        { id: 'l_2', name: 'Mom & Kids', order: 2, slots: [{ id: 's1', name: 'Mom Title', categories: [], top: 10, left: 20, width: 60, height: 20, isLocked: true }, { id: 's2', name: 'Kid', categories: ['Girls'], top: 40, left: 50, width: 30, height: 40 }] }
      ],
      assets: {
        clipart: {
          '201': { selected: true, customCategory: 'Mom' },
          '202': { selected: true, customCategory: 'Girls' },
          '203': { selected: true, customCategory: 'Boys' }
        }
      }
    };
    const mockC = [
      { id: '201', name: 'Super Mom', category: 'Mom', thumb: 'https://placehold.co/200x200/white/333?text=Mom' },
      { id: '202', name: 'Big Sister', category: 'Girls', thumb: 'https://placehold.co/200x200/white/333?text=Girl' },
      { id: '203', name: 'Little Brother', category: 'Boys', thumb: 'https://placehold.co/200x200/white/333?text=Boy' }
    ];
    processData(mockConfig, { cliparts: mockC });
  };

  const applyLayout = (num, availableLayouts = layouts) => {
    const layout = availableLayouts.find(l => l.order === num) || availableLayouts.find(l => l.id === num);
    if (layout && layout.slots) {
      setSlots(layout.slots.map(s => ({ 
        ...s, 
        clipart: s.defaultImage ? { thumb: s.defaultImage } : null,
        userFlipX: s.flipX || false 
      })));
      
      const firstUnlocked = layout.slots.find(s => !s.isLocked);
      if (firstUnlocked) {
        setActiveSlotId(firstUnlocked.id);
        setActiveCategory(firstUnlocked.categories?.length === 1 ? firstUnlocked.categories[0] : null);
      } else {
        setActiveSlotId(null);
      }
    } else {
      setSlots([]);
      setActiveSlotId(null);
    }
  };

  const categoryTiles = useMemo(() => {
    if (!config) return [];
    const savedClipart = config.assets?.clipart || {};
    const cats = {};
    Object.keys(savedClipart).forEach(id => {
      const item = savedClipart[id];
      if (item.selected && item.customCategory) {
        if (!cats[item.customCategory]) {
          cats[item.customCategory] = { name: item.customCategory, order: config.category_order?.[item.customCategory] || 0, items: [] };
        }
        const full = lumiseData.clipart.find(c => String(c.id) === String(id));
        if (full) cats[item.customCategory].items.push(full);
      }
    });
    return Object.values(cats).sort((a, b) => a.order - b.order);
  }, [config, lumiseData.clipart]);

  const sizeList = useMemo(() => (config?.sizes || '').split(',').map(s => s.trim()).filter(Boolean), [config]);

  const activeSlot = slots.find(s => s.id === activeSlotId);
  const activeAllowedCats = activeSlot?.categories || [];

  const availableCategoryTiles = useMemo(() => {
    if (activeAllowedCats.length === 0) return categoryTiles; 
    return categoryTiles.filter(cat => activeAllowedCats.includes(cat.name));
  }, [categoryTiles, activeAllowedCats]);


  // --- Interactions ---
  const handleSlotClick = (slot) => {
    if (slot.isLocked) return; 
    setActiveSlotId(slot.id);
    
    if (slot.categories && slot.categories.length === 1) {
      setActiveCategory(slot.categories[0]);
    } else {
      setActiveCategory(null); 
    }
  };

  const handleClipartClick = (clipart) => {
    if (!activeSlotId) return;
    setSlots(prev => prev.map(slot => 
      slot.id === activeSlotId ? { ...slot, clipart } : slot
    ));
    
    const nextEmpty = slots.find(s => s.id !== activeSlotId && !s.isLocked && (!s.clipart || s.clipart.thumb === s.defaultImage));
    if (nextEmpty) {
      setActiveSlotId(nextEmpty.id);
      setActiveCategory(nextEmpty.categories?.length === 1 ? nextEmpty.categories[0] : null);
    }
  };

  const toggleFlip = () => {
    if (!activeSlotId) return;
    setSlots(prev => prev.map(slot => 
      slot.id === activeSlotId ? { ...slot, userFlipX: !slot.userFlipX } : slot
    ));
  };

  const generateCustomImage = async () => {
    setIsGenerating(true);
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 1000; 
      canvas.height = 1000;
      
      const drawPromises = slots.filter(s => s.clipart).map(slot => {
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const slotX = (slot.left / 100) * canvas.width;
            const slotY = (slot.top / 100) * canvas.height;
            const slotW = (slot.width / 100) * canvas.width;
            const slotH = (slot.height / 100) * canvas.height;
            
            const imgW = img.naturalWidth || img.width;
            const imgH = img.naturalHeight || img.height;
            const scale = Math.min(slotW / imgW, slotH / imgH);
            
            const drawW = imgW * scale;
            const drawH = imgH * scale;
            
            const finalX = slotX + (slotW - drawW) / 2;
            const finalY = slotY + (slotH - drawH) / 2;
            
            ctx.save();
            if (slot.userFlipX) {
              ctx.scale(-1, 1);
              ctx.drawImage(img, -finalX - drawW, finalY, drawW, drawH);
            } else {
              ctx.drawImage(img, finalX, finalY, drawW, drawH);
            }
            ctx.restore();
            resolve();
          };
          img.onerror = resolve;
          img.src = slot.clipart.thumb;
        });
      });

      await Promise.all(drawPromises);
      const dataUrl = canvas.toDataURL('image/png');
      const total = Object.values(quantities).reduce((a, b) => a + b, 0);
      const price = parseFloat(config?.price) || 0;
      const wooProductId = config?.woo_product_id || window.EG_FRONTEND?.woo_product_id || 0;
      const activeBase = productBases.find(b => b.id === activeBaseId);
      
      const formData = new FormData();
      formData.append('action', 'eg_add_to_cart');
      formData.append('product_id', wooProductId);
      formData.append('base_color', activeBase?.name || 'Default');
      formData.append('quantities', JSON.stringify(quantities));
      formData.append('image_data', dataUrl);

      const fallbackBaseUrl = typeof window !== 'undefined' && window.EG_FRONTEND?.rest_url ? window.EG_FRONTEND.rest_url.split('/wp-json')[0] : '';
      const fallbackAjaxUrl = fallbackBaseUrl ? `${fallbackBaseUrl}/wp-admin/admin-ajax.php` : '/wp-admin/admin-ajax.php';
      const ajaxUrl = (typeof window !== 'undefined' && window.EG_FRONTEND?.ajax_url) ? window.EG_FRONTEND.ajax_url : fallbackAjaxUrl;
      
      const response = await fetch(ajaxUrl, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server responded with HTTP error:", response.status, errorText);
        throw new Error(`Server returned status ${response.status}. This may be caused by a missing endpoint or a server firewall blocking the image payload.`);
      }

      const textResult = await response.text();
      let result;
      try {
        result = JSON.parse(textResult);
      } catch (e) {
        console.error("Invalid JSON from server:", textResult);
        throw new Error("Server did not return valid JSON. Check console for raw response.");
      }
      
      if (result.success) {
        window.location.href = result.data.redirect_url || '/cart';
      } else {
        alert(result.data?.message || 'Error adding to cart. Ensure WooCommerce product ID is set correctly in backend.');
      }
      
    } catch (err) {
      console.error("Error generating image", err);
      alert(`There was an error compiling your design or connecting to the cart:\n\n${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#64748b', fontWeight: 'bold', fontFamily: 'sans-serif' }}>
      {typeof window !== 'undefined' && window.EG_FRONTEND?.loading_text ? window.EG_FRONTEND.loading_text : "LOADING GENERATOR..."}
    </div>
  );
  
  const accent = config?.accent_color || '#2563eb';
  const basePrice = parseFloat(config?.price) || 0;
  const totalQty = Object.values(quantities).reduce((a, b) => a + b, 0);
  
  const activeBase = productBases.find(b => b.id === activeBaseId);
  const canvasBaseImage = activeBase?.image || 'https://placehold.co/800x800/transparent/eee?text=Apparel+Base';

  // Customizable font sizes from backend
  const titleSizeStyle = config?.title_font_size ? { fontSize: `${config.title_font_size}px` } : {};
  const welcomeSizeStyle = config?.welcome_font_size ? { fontSize: `${config.welcome_font_size}px` } : {};

  // Custom positioning of the invisible canvas area over the product base
  const canvasAreaStyle = {
    position: 'absolute',
    top: `${config?.canvas_top !== undefined ? config.canvas_top : 22}%`,
    left: `${config?.canvas_left !== undefined ? config.canvas_left : 22}%`,
    width: `${config?.canvas_width !== undefined ? config.canvas_width : 56}%`,
    height: `${config?.canvas_height !== undefined ? config.canvas_height : 56}%`
  };

  return (
    <div 
      className="w-full min-h-screen md:h-screen overflow-y-auto md:overflow-hidden flex flex-col font-sans text-slate-800 relative"
      style={{ 
        backgroundImage: config?.background_image ? `url(${config.background_image})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#f1f5f9',
        '--eg-accent': accent,
        '--eg-accent-light': `${accent}88`
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: frontendStyles }} />
      {config?.background_image && <div className="absolute inset-0 bg-white/40 backdrop-blur-sm pointer-events-none z-0" />}

      {/* TOP HEADER: Full width Title */}
      <header className="relative z-20 w-full py-4 md:py-6 px-4 md:px-10 border-b border-white/20 bg-white/60 backdrop-blur-md shadow-sm flex flex-col items-center shrink-0">
        <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter italic text-center text-slate-800 drop-shadow-md" style={{ ...titleSizeStyle, lineHeight: 1.1 }}>
          {typeof window !== 'undefined' && window.EG_FRONTEND?.title ? window.EG_FRONTEND.title : 'Forever My Baby Clubhouse'}
        </h1>
        {typeof window !== 'undefined' && window.EG_FRONTEND?.welcome_text && (
          <p className="mt-2 text-slate-600 font-medium max-w-2xl text-center" style={{ ...welcomeSizeStyle }}>
            {window.EG_FRONTEND.welcome_text}
          </p>
        )}
      </header>

      {/* Responsive layout wrapper */}
      <div className="flex-1 flex flex-col md:flex-row relative z-10 w-full overflow-y-visible md:overflow-hidden">
        
        {/* --- LEFT SIDEBAR: DRILL-DOWN CATEGORIES & LAYOUT SELECT --- */}
        <aside className="w-auto md:w-80 flex flex-col bg-white/95 backdrop-blur-md shadow-2xl md:border-r border-white/50 z-30 m-4 md:m-6 rounded-3xl overflow-hidden shrink-0 order-2 md:order-none">
          <div className="p-6 border-b border-slate-100 flex-shrink-0 bg-slate-50/50">
            {config?.template_mode === 'question' ? (
              <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100">
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">{config?.template_question || "Pick a number"}</label>
                <input 
                  type="number" min="1" max={layouts.length > 0 ? layouts.length : 1}
                  value={templateNumber}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    if (val > layouts.length) return; // Enforce max
                    setTemplateNumber(val);
                    applyLayout(val);
                    setActiveCategory(null);
                  }}
                  className="w-full bg-slate-50 border-none rounded-lg p-2 text-xl font-black text-center outline-none focus:ring-2 transition-all"
                  style={{ color: accent, '--tw-ring-color': accent }}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                 <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Choose Layout</label>
                 <div className="grid grid-cols-2 gap-2">
                   {[...layouts].sort((a,b)=>a.order-b.order).map(l => (
                     <button
                       key={l.id}
                       onClick={() => { applyLayout(l.id); setActiveCategory(null); }}
                       className={`p-2 text-xs font-bold rounded-lg transition-all border ${slots.length > 0 && slots[0]?.id && l.slots.some(s => s.id === slots[0].id) ? 'text-white shadow-md' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                       style={{ backgroundColor: slots.length > 0 && slots[0]?.id && l.slots.some(s => s.id === slots[0].id) ? accent : undefined }}
                     >
                       {l.name}
                     </button>
                   ))}
                 </div>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto hide-scrollbar p-6 max-h-[40vh] md:max-h-none min-h-0">
            {!activeCategory ? (
              <div className="flex flex-col gap-3 pb-8">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-1 mb-1">
                  {activeAllowedCats.length > 0 ? `Allowed Options For: ${activeSlot?.name}` : "Select an Option"}
                </label>
                {availableCategoryTiles.map(cat => (
                  <button 
                    key={cat.name}
                    type="button"
                    onClick={() => setActiveCategory(cat.name)}
                    className="w-full text-left px-5 py-4 rounded-xl font-black uppercase text-sm text-slate-700 bg-slate-50 hover:bg-slate-100 transition-all border border-slate-100 shadow-sm flex justify-between items-center group"
                  >
                    <span>{cat.name}</span>
                    <span className="text-slate-300 group-hover:text-slate-500">→</span>
                  </button>
                ))}
                {availableCategoryTiles.length === 0 && (
                  <div className="text-xs text-center text-slate-400 py-10">No options available for this slot.</div>
                )}
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <button 
                    type="button"
                    onClick={() => setActiveCategory(null)}
                    className="flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-slate-700 transition-colors"
                  >
                    <Icon name="chevronLeft" size={14} /> Back
                  </button>
                  <button
                    type="button"
                    onClick={toggleFlip}
                    className="flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all shadow-sm"
                    title="Flip Image Horizontally"
                  >
                    <Icon name="flip" size={12} /> Flip
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-3 pb-8">
                  {categoryTiles.find(c => c.name === activeCategory)?.items.map(clip => (
                    <button
                      key={clip.id}
                      type="button"
                      title={clip.name}
                      onClick={() => handleClipartClick(clip)}
                      className="aspect-square bg-slate-50 rounded-xl border border-slate-100 hover:border-blue-400 hover:shadow-md transition-all p-3 flex items-center justify-center overflow-hidden"
                    >
                      <img src={clip.thumb} alt={clip.name} className="w-full h-full object-contain pointer-events-none" />
                    </button>
                  ))}
                  {(!categoryTiles.find(c => c.name === activeCategory)?.items.length) && (
                    <div className="col-span-2 text-center text-xs text-slate-400 py-10">No items found in this category.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* --- DOMINANT CENTER CANVAS --- */}
        <main className="flex-1 relative flex items-center justify-center p-4 md:p-8 overflow-hidden min-h-[50vh] md:min-h-0 order-1 md:order-none">
           <div className="relative w-full h-full max-h-full aspect-square flex items-center justify-center max-w-4xl">
              <img 
                src={canvasBaseImage} 
                alt="Product Base" 
                className="absolute inset-0 w-full h-full object-contain pointer-events-none transition-all duration-300"
                style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.1))' }}
              />
              
              {/* Custom Adjustable Canvas Region */}
              <div className="pointer-events-none" style={canvasAreaStyle}>
                 <div className="relative w-full h-full pointer-events-auto">
                   {slots.map(slot => (
                     <div 
                       key={slot.id}
                       onClick={() => handleSlotClick(slot)}
                       className={`eg-slot ${activeSlotId === slot.id ? 'active' : ''} ${slot.isLocked ? 'locked pointer-events-none' : ''}`}
                       style={{ top: `${slot.top}%`, left: `${slot.left}%`, width: `${slot.width}%`, height: `${slot.height}%` }}
                     >
                       {slot.clipart ? (
                         <img src={slot.clipart.thumb} alt="Design" style={{ transform: slot.userFlipX ? 'scaleX(-1)' : 'none' }} />
                       ) : (
                         <span className="eg-slot-empty-text">{!slot.isLocked ? slot.name : ''}</span>
                       )}
                     </div>
                   ))}
                 </div>
              </div>
           </div>
        </main>

        {/* --- RIGHT SIDEBAR: COLORS & ORDER PANEL --- */}
        <aside className="w-auto md:w-80 bg-white/95 backdrop-blur-md shadow-2xl md:border-l border-white/50 z-30 flex flex-col m-4 md:m-6 rounded-3xl overflow-hidden shrink-0 order-3 md:order-none">
          
          {/* Color Base Swatches */}
          {productBases.length > 0 && (
            <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex-shrink-0">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">Product Color</h3>
              <div className="flex flex-wrap gap-3">
                {productBases.map(base => (
                  <button
                    key={base.id}
                    type="button"
                    onClick={() => setActiveBaseId(base.id)}
                    className={`w-10 h-10 rounded-full border-2 shadow-sm transition-all ${activeBaseId === base.id ? 'scale-110 ring-2 ring-offset-2' : 'hover:scale-105'}`}
                    style={{ backgroundColor: base.color, borderColor: 'white', '--tw-ring-color': accent }}
                    title={base.name}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 p-6 flex flex-col min-h-0 max-h-[60vh] md:max-h-none">
            <div className="flex justify-between items-end mb-6 pb-4 border-b-2 border-slate-50 flex-shrink-0">
              <div>
                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Order Details</h3>
                {basePrice > 0 && <div className="text-xl font-black text-slate-800 mt-1">${basePrice.toFixed(2)} <span className="text-xs text-slate-400 font-normal">/ea</span></div>}
              </div>
              <span className="text-[10px] font-black text-slate-300 mb-1">QTY</span>
            </div>
            
            <div className="space-y-3 mb-6 flex-1 overflow-y-auto hide-scrollbar pb-4">
              {sizeList.map(size => (
                <div key={size} className="flex items-center justify-between bg-slate-50 rounded-lg p-2 px-4">
                  <span className="text-lg font-black text-slate-700">{size}</span>
                  <input 
                    type="number" min="0"
                    value={quantities[size] || ''}
                    onChange={(e) => setQuantities({ ...quantities, [size]: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    className="w-16 bg-white border border-slate-200 rounded text-center font-bold py-1 outline-none focus:border-blue-500"
                  />
                </div>
              ))}
            </div>

            <button 
              type="button"
              onClick={generateCustomImage}
              disabled={isGenerating || totalQty === 0}
              className="w-full py-4 rounded-xl text-white font-black text-lg shadow-xl hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 transition-all flex flex-col items-center justify-center gap-1 flex-shrink-0 mt-auto"
              style={{ backgroundColor: accent }}
            >
              {isGenerating ? (
                <span className="animate-pulse py-3">PROCESSING...</span>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <Icon name="shoppingCart" size={20} />
                    <span>ADD TO CART</span>
                  </div>
                  {basePrice > 0 && totalQty > 0 && (
                    <div className="text-xs font-bold opacity-90">
                      Total: ${(totalQty * basePrice).toFixed(2)}
                    </div>
                  )}
                </>
              )}
            </button>
          </div>
        </aside>

      </div>
    </div>
  );
};

// Safe mounting logic for WordPress standard page integrations
if (typeof window !== 'undefined' && window.EG_FRONTEND) {
  const mountApp = () => {
    const container = document.getElementById('eg-frontend-root');
    if (container) {
      const loader = document.getElementById('eg-initial-loader');
      if (loader) loader.style.display = 'none';
      const root = createRoot(container);
      root.render(<FrontendApp />);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountApp);
  } else {
    mountApp();
  }
}

export default FrontendApp;